import React from 'react';
import OakStatusBadge from './compliance/OakStatusBadge';
import { ECO_ACORN, OAK_STATUS, isEcoStage, ecoStageToFrame, ecoStageLabel } from '../lib/ecoAward';

// Tiered supplier badge — Bronze / Silver / Gold. Pure SVG so it scales
// crisply to any size and stays sharp on retina. The shape is a hex shield
// using Fusion's 30° chevron geometry; gradients are inlined per instance
// (no shared root-level <defs>) to keep the component self-contained.
//
// Usage:
//   <Badge tier="silver" size="lg" expired={false} />
//   <Badge tier="gold" size="xl" categoryLabel="Eco-Leader" />
//
// Sizes:
//   xl  120 × 138  — earn modal, gallery
//   lg   80 ×  92  — portal achievements header
//   md   48 ×  55  — admin supplier detail strip
//   sm   28 ×  32  — inline chip / small contexts

const SIZE_DIMS = {
  xl: { w: 120, h: 138 },
  lg: { w: 80,  h: 92  },
  md: { w: 48,  h: 55  },
  sm: { w: 28,  h: 32  },
};

// Gradient stops per tier. Each instance generates a unique gradient
// id so multiple badges on the same page don't collide. The deep stop
// at the end provides depth on the lower-right edge of the hex.
const GRADIENT_STOPS = {
  bronze: [
    { off: '0%',   color: '#E89D5C' },
    { off: '50%',  color: '#CD7F32' },
    { off: '100%', color: '#8B5A1F' },
  ],
  silver: [
    { off: '0%',   color: '#E8E8E8' },
    { off: '50%',  color: '#C0C0C0' },
    { off: '100%', color: '#8A8A8A' },
  ],
  gold: [
    { off: '0%',   color: '#F4D26B' },
    { off: '50%',  color: '#E5B941' },
    { off: '100%', color: '#A88323' },
  ],
};
const STROKES = {
  bronze: '#B27330',
  silver: '#A5A5A5',
  gold:   '#C4A036',
};
const LABEL_COLOURS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold:   '#E5B941',
};

let badgeIdCounter = 0;
const nextBadgeId = () => `badge-${++badgeIdCounter}`;

export default function Badge({
  tier = 'bronze',
  size = 'md',
  expired = false,
  locked = false,          // unearned recognition — dashed outline + muted icon
  categoryLabel,           // optional small label rendered beneath the SVG
  categorySlug,            // category identity; 'eco' swaps the hex shield
                           // for the growing-oak illustration
  tierName,                // optional override of the tier label (e.g. 'Eco-Leader')
  showLabel = false,       // render the tier/category text beneath the SVG
  ariaLabel,
}) {
  const dims = SIZE_DIMS[size] || SIZE_DIMS.md;
  const stops = GRADIENT_STOPS[tier] || GRADIENT_STOPS.bronze;
  const stroke = STROKES[tier] || STROKES.bronze;
  const labelColor = LABEL_COLOURS[tier] || LABEL_COLOURS.bronze;
  // Unique id per render so multiple Badge instances coexist without
  // gradient cross-bleed. Stable across re-renders of the same instance
  // via useRef would also work; this is plenty unique for our scale.
  const gradId = React.useMemo(nextBadgeId, []);
  const fill = `url(#${gradId})`;
  const opacity = expired ? 0.4 : 1;
  const filter = expired ? 'grayscale(100%)' : undefined;

  // Eco branch — the Eco recognition award is the growing-oak "Oak Status"
  // progression, not a metal tier. The supplier_badges row stores its oak
  // STAGE (acorn…oak_status) in `tier`; map that to an oak frame. Locked /
  // unearned = acorn (0 eco docs approved). Oak Status carries the vibrant
  // Fusion-Green label (positive outcome). All OTHER categories keep the hex.
  if (categorySlug === 'eco') {
    const ecoStage = locked ? ECO_ACORN : (isEcoStage(tier) ? tier : ECO_ACORN);
    const ecoFrame = ecoStageToFrame(ecoStage);
    const ecoLabelColor = ecoStage === OAK_STATUS ? '#92D400' : '#9a9d90';
    return (
      <span className="inline-flex flex-col items-center" aria-label={ariaLabel}>
        <span style={{ display: 'block', opacity: locked ? 0.62 : opacity, filter }}>
          <OakStatusBadge frame={ecoFrame} size={dims.w} animate={false} />
        </span>
        {showLabel && (
          <span className="mt-2 text-center">
            <span className="block font-['Metropolis'] text-[9px] font-extrabold tracking-[0.25em] uppercase" style={{ color: ecoLabelColor }}>
              {ecoStageLabel(ecoStage)}
            </span>
            {categoryLabel && (
              <span className="block font-['Metropolis'] text-[10px] font-semibold text-fusion-mid-grey mt-0.5">
                {categoryLabel}
              </span>
            )}
          </span>
        )}
      </span>
    );
  }

  // Locked branch — dashed outline + muted leaf icon, no metallic fill,
  // no chevrons, no tier dots. Used for "available but unearned"
  // recognition badges so they look visibly different from the
  // metallic earned state without borrowing a semantic colour. Per
  // the recognition / compliance brief: "earned vs available is
  // carried by metallic-fill vs locked-outline, not by hue."
  if (locked) {
    return (
      <span className="inline-flex flex-col items-center" aria-label={ariaLabel}>
        <svg
          width={dims.w}
          height={dims.h}
          viewBox="0 0 120 138"
          style={{ display: 'block', opacity: 0.62 }}
          aria-hidden="true">
          <path
            d="M 60 4 L 110 22 L 110 78 L 60 134 L 10 78 L 10 22 Z"
            fill="none"
            stroke="#6c6f64"
            strokeWidth="2.5"
            strokeDasharray="6 5" />
          {/* Muted leaf icon — same shape as the earned state so the
              progression read is obvious, just desaturated. */}
          <g transform="translate(60, 60)">
            <path d="M 0 -16 Q 16 -8 16 8 Q 16 24 0 24 Q -16 24 -16 8 Q -16 -8 0 -16 Z" fill="none" stroke="#9a9d90" strokeWidth="2.5" />
            <path d="M 0 -12 L 0 20" stroke="#9a9d90" strokeWidth="2" />
          </g>
        </svg>
        {showLabel && (
          <span className="mt-2 text-center">
            <span className="block font-['Metropolis'] text-[9px] font-extrabold tracking-[0.25em] uppercase text-[#6c6f64]">
              locked
            </span>
            {(tierName || categoryLabel) && (
              <span className="block font-['Metropolis'] text-[10px] font-semibold text-fusion-mid-grey mt-0.5">
                {tierName || categoryLabel}
              </span>
            )}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col items-center" aria-label={ariaLabel}>
      <svg
        width={dims.w}
        height={dims.h}
        viewBox="0 0 120 138"
        style={{ display: 'block', opacity, filter }}
        aria-hidden="true">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            {stops.map(s => <stop key={s.off} offset={s.off} stopColor={s.color} />)}
          </linearGradient>
        </defs>
        {/* Subtle outer glow for Gold — slightly larger transparent fill
            behind the main shape. Bronze + Silver skip this to keep them
            visually distinct from the top tier. */}
        {tier === 'gold' && !expired && (
          <path d="M 60 0 L 114 20 L 114 80 L 60 138 L 6 80 L 6 20 Z" fill={fill} opacity="0.2" />
        )}
        {/* Outer hex shield */}
        <path
          d="M 60 4 L 110 22 L 110 78 L 60 134 L 10 78 L 10 22 Z"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.5" />
        {/* Sheen — top-half polygon at 18% white. Matches the metallic
            treatment on ComplianceBadge.green so recognition badges
            and the compliant hexagon share one "earned metal" look. */}
        {!expired && (
          <path d="M 60 4 L 110 22 L 60 56 L 10 22 Z" fill="#ffffff" opacity="0.18" />
        )}
        {/* Inner double chevron — Fusion brand device */}
        <path d="M 30 50 L 60 70 L 90 50" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="square" opacity="0.9" />
        <path d="M 30 65 L 60 85 L 90 65" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="square" opacity="0.9" />
        {/* Leaf icon — sustainability mark. Silver + Gold get extra
            leaf stems to read as "more growth = higher tier". */}
        <g transform="translate(60, 30)">
          <path d="M 0 -8 Q 8 -4 8 4 Q 8 12 0 12 Q -8 12 -8 4 Q -8 -4 0 -8 Z" fill="white" opacity="0.95" />
          <path d="M 0 -6 L 0 10" stroke={stroke} strokeWidth="1.2" opacity="0.7" />
          {(tier === 'silver' || tier === 'gold') && (
            <path d="M 5 -2 Q 10 0 10 5" stroke="white" strokeWidth="1.2" fill="none" opacity="0.9" />
          )}
          {tier === 'gold' && (
            <path d="M -5 -2 Q -10 0 -10 5" stroke="white" strokeWidth="1.2" fill="none" opacity="0.9" />
          )}
        </g>
        {/* Tier indicator dots — Bronze: none, Silver: 2, Gold: 3 */}
        {tier === 'silver' && (
          <>
            <circle cx="48" cy="100" r="2.5" fill="white" opacity="0.9" />
            <circle cx="60" cy="100" r="2.5" fill="white" opacity="0.9" />
          </>
        )}
        {tier === 'gold' && (
          <>
            <circle cx="48" cy="100" r="2.5" fill="white" opacity="0.95" />
            <circle cx="60" cy="100" r="2.5" fill="white" opacity="0.95" />
            <circle cx="72" cy="100" r="2.5" fill="white" opacity="0.95" />
          </>
        )}
      </svg>
      {showLabel && (
        <span className="mt-2 text-center">
          <span
            className="block font-['Metropolis'] text-[9px] font-extrabold tracking-[0.25em] uppercase"
            style={{ color: labelColor }}>
            {tier}
          </span>
          {(tierName || categoryLabel) && (
            <span className="block font-['Metropolis'] text-[10px] font-semibold text-fusion-mid-grey mt-0.5">
              {tierName || categoryLabel}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
